import { syntheticOperationalContext } from "@reminder-tracker/testing";
import type { OperationalContextView } from "./contracts";
import {
  AdministrationAuditDetails,
  AdministrationImpactPanel,
  AdministrationPageHeader,
  AdministrationStatusSummary,
} from "../../ui/administration-surface";

const localView: OperationalContextView = {
  context: syntheticOperationalContext,
  timeline: [
    {
      occurredAt: "23 Aug 2026 · 15:10",
      area: "Environment classification",
      outcome: "Healthy",
      correlationReference: "corr_•••8a1",
      evidence: "Local · synthetic-only",
    },
    {
      occurredAt: "23 Aug 2026 · 15:08",
      area: "Public configuration",
      outcome: "Healthy",
      correlationReference: "corr_•••74c",
      evidence: "2 allowlisted names",
    },
    {
      occurredAt: "23 Aug 2026 · 15:04",
      area: "Provider connectivity",
      outcome: "Review",
      correlationReference: "corr_•••2d0",
      evidence: "Intentionally disconnected",
    },
  ],
};

export function OperationalContextScreen(): React.JSX.Element {
  return (
    <>
      <AdministrationPageHeader
        title="What needs your attention"
        purpose="Start with local service impact, then review synthetic activity and control evidence."
        meta={<span className="environment-badge">Local foundation · synthetic only</span>}
      />
      <AdministrationStatusSummary
        items={[
          {
            label: "Needs action",
            tone: "action",
            value: 0,
            title: "External service incidents",
            description: "No external providers are connected in this local foundation.",
          },
          {
            label: "Monitor",
            tone: "monitor",
            value: 1,
            title: "Intentional disconnection",
            description: "Provider connectivity remains disconnected by design.",
          },
          {
            label: "Healthy",
            tone: "healthy",
            value: 2,
            title: "Local controls",
            description: "Environment classification and public configuration are recorded.",
          },
        ]}
      />
      <AdministrationImpactPanel
        label="Service impact"
        title="External delivery is intentionally unavailable in local development"
        description="Use this screen only for synthetic interface and control work. It is not evidence about Shared Test delivery."
        tone="monitor"
      />
      <section aria-label="Local aggregate summary" className="summary">
        <div className="measure">
          <strong>{localView.context.householdCount}</strong>
          <span>Synthetic households</span>
        </div>
        <div className="measure">
          <strong>{localView.context.adultCount}</strong>
          <span>Synthetic adults</span>
        </div>
        <div className="measure">
          <strong>{localView.context.trackedRenewalCount}</strong>
          <span>Synthetic renewals</span>
        </div>
        <div className="measure">
          <strong>{localView.context.reminderOccurrenceCount}</strong>
          <span>Synthetic occurrences recorded</span>
        </div>
        <div className="measure">
          <strong>0</strong>
          <span>External providers connected</span>
        </div>
      </section>
      <AdministrationAuditDetails summary="Recent local control lineage and masked correlations">
        <section aria-labelledby="lineage-title">
          <div className="section-head">
            <h2 id="lineage-title">Recent control lineage</h2>
            <span>Identifiers masked by design</span>
          </div>
          <table className="timeline">
            <thead>
              <tr>
                <th>Recorded</th>
                <th>Control area</th>
                <th>Outcome</th>
                <th>Correlation</th>
                <th>Evidence</th>
              </tr>
            </thead>
            <tbody>
              {localView.timeline.map((entry) => (
                <tr key={entry.correlationReference}>
                  <td data-label="Recorded">
                    <time>{entry.occurredAt}</time>
                  </td>
                  <td data-label="Control area">{entry.area}</td>
                  <td data-label="Outcome">
                    <span className={`state ${entry.outcome === "Review" ? "review" : ""}`}>
                      {entry.outcome}
                    </span>
                  </td>
                  <td className="masked" data-label="Correlation">
                    {entry.correlationReference}
                  </td>
                  <td data-label="Evidence">{entry.evidence}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <p className="audit-boundary">
          No household payload, identity provider or Cloud backend is available in this local view.
        </p>
      </AdministrationAuditDetails>
    </>
  );
}
